import { getCarrierImageBlob } from '../../apiCalls/images/imageApiCalls';
import { useForceRender } from '../react-helpers/useForceRender';
import { CarrierId } from '../../types/api/primaryKeys/PrimaryKey';

// Note: While this would require back end improvements, there is 0 reason
// why we need a file name to pull carrier images. The back end should be able to resolve a logo
// file based on just a carrier id. That should be the improvement to make if this becomes an issue
// when implementing in other spots.
interface RequiredFieldsToPopulateCarrierCache {
    readonly carrierId: CarrierId,
    readonly carrierLogoFileName: string
}

// Context may be better, but this works for now.
// This is meant to be a global cache of carrier images that expires on full browser reload.
const globalCarrierImageCache = new Map<CarrierId, string>();

// A global cache to keep track of in progress requests.
const globalCarrierInProgressRequests = new Map<CarrierId, Promise<Blob>>();

// NOTE: The below may look more complicated than it needs to be. Let me explain the goals below:

// * Images are cached for the duration of a "session" (right now that means between browser refreshes).
// * Multiple components can connect to this hook without repeating requests to the same carrier.

// YES there is probably a better way to do this. But this works well as is.

export const useCarrierImages = () => {

    // In the case that new carrier images are fetched,
    // we will want to force the calling component to re-render itself to pick up these images.
    const forceComponentRender = useForceRender();

    const updateCarrierImages = async (carrierUpdateData: readonly RequiredFieldsToPopulateCarrierCache[]) => {
    
        // Find which of the carrier ids supplied don't exist in cache already.
        // Note: because of code that might reference this without TypeScript, I have to
        // Include checks that the carrier id and carrier logo file name are valid...
        const carriersMissingFromCache = carrierUpdateData.filter(c => {
            if (!c.carrierId || !c.carrierLogoFileName) {
                return false;
            }
            
            return getCarrierImageUrl(c.carrierId) === null;
        });
    
        // Bail and do nothing if there's no new images to fetch.
        if (carriersMissingFromCache.length === 0) {
            return;
        }

        // Below this point, we know that the component needs to await promises and re-render.

        const alreadyInProgressCarrierIds = Array.from(globalCarrierInProgressRequests.keys());
        
        // Carriers to fetch are the ones missing from the cache that aren't already in progress.
        const newCarriersToFetch = carriersMissingFromCache.filter(c => alreadyInProgressCarrierIds.find(i => i === c.carrierId) === undefined);

        // This actually sets off all of the missing API requests.
        newCarriersToFetch.forEach(c => globalCarrierInProgressRequests.set(c.carrierId, getCarrierImageBlob(c.carrierId, c.carrierLogoFileName)));

        try { 
            // Capture all of the promises we need to wait on.
            const carrierFetchPromises = Array.from(globalCarrierInProgressRequests.entries());
        
            // Wait until all requests are completed.
            const awaitedPromises = await Promise.all(carrierFetchPromises.map(c => c[1]));
        
            // Store the results into the cache.
            awaitedPromises.forEach((imageBlob, index) => {
                const objectUrl = URL.createObjectURL(imageBlob);
                globalCarrierImageCache.set(carrierFetchPromises[index][0], objectUrl);
            });
        
            // We are assuming that any component that called this hook also will want to redraw itself
            // after it has loaded new carrier images. This can be a configuration setting as a future update if needed,
            // but seems unneeded right now.
            forceComponentRender();
        }
        finally {
            newCarriersToFetch.forEach(c => globalCarrierInProgressRequests.delete(c.carrierId));
        }
    };

    return {
        getCarrierImageUrl,
        updateCarrierImages
    };
};

const getCarrierImageUrl = (carrierId: CarrierId) => {
    const result = globalCarrierImageCache.get(carrierId);
    return result ?? null;
};



